import { useEffect, useState, useRef } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { CheckCircle2, Loader2, XCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { motion } from 'framer-motion';

const API = process.env.REACT_APP_BACKEND_URL;

export default function UpgradeSuccessPage() {
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get('session_id');
  const [status, setStatus] = useState('checking');
  const [planName, setPlanName] = useState('');
  const navigate = useNavigate();
  const { token, checkAuth } = useAuth();
  const polledRef = useRef(false);

  useEffect(() => {
    if (!sessionId || !token || polledRef.current) return;
    polledRef.current = true;

    let attempts = 0;
    const maxAttempts = 8;
    const pollInterval = 2000;

    async function pollStatus() {
      try {
        const res = await fetch(`${API}/payments/checkout-status/${sessionId}`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        const data = await res.json();

        if (data.payment_status === 'paid') {
          setStatus('success');
          setPlanName(data.metadata?.plan_name || 'your new plan');
          await checkAuth();
          return;
        }
        if (data.status === 'expired') {
          setStatus('expired');
          return;
        }

        attempts++;
        if (attempts >= maxAttempts) {
          setStatus('timeout');
          return;
        }
        setTimeout(pollStatus, pollInterval);
      } catch {
        attempts++;
        if (attempts >= maxAttempts) {
          setStatus('error');
          return;
        }
        setTimeout(pollStatus, pollInterval);
      }
    }

    pollStatus();
  }, [sessionId, token, checkAuth]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#F8FAFF] px-4">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="w-full max-w-md text-center"
      >
        {status === 'checking' && (
          <div data-testid="upgrade-checking">
            <Loader2 className="w-16 h-16 text-indigo-500 animate-spin mx-auto mb-6" />
            <h1 className="text-2xl font-bold text-[#0F172A] mb-2">Processing your payment...</h1>
            <p className="text-[#64748B]">Please wait while we confirm your upgrade.</p>
          </div>
        )}

        {status === 'success' && (
          <div data-testid="upgrade-success">
            <div className="w-20 h-20 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-6">
              <CheckCircle2 className="w-10 h-10 text-green-600" />
            </div>
            <h1 className="text-2xl font-bold text-[#0F172A] mb-2">Upgrade Complete!</h1>
            <p className="text-[#64748B] mb-8">
              You've been upgraded to the <span className="font-semibold text-indigo-600">{planName}</span> plan.
            </p>
            <Button
              data-testid="go-to-dashboard-btn"
              onClick={() => navigate('/dashboard', { replace: true })}
              className="bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl h-12 px-8 font-semibold shadow-md"
            >
              Go to Dashboard
            </Button>
          </div>
        )}

        {(status === 'expired' || status === 'error' || status === 'timeout') && (
          <div data-testid="upgrade-failed">
            <div className="w-20 h-20 rounded-full bg-red-100 flex items-center justify-center mx-auto mb-6">
              <XCircle className="w-10 h-10 text-red-500" />
            </div>
            <h1 className="text-2xl font-bold text-[#0F172A] mb-2">
              {status === 'expired' ? 'Session Expired' : 'Something went wrong'}
            </h1>
            <p className="text-[#64748B] mb-8">
              {status === 'expired'
                ? 'Your payment session has expired. Please try again.'
                : 'We couldn\'t confirm your payment. Please check your settings or try again.'}
            </p>
            <Button
              data-testid="retry-upgrade-btn"
              onClick={() => navigate('/settings', { replace: true })}
              className="bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl h-12 px-8 font-semibold shadow-md"
            >
              Back to Settings
            </Button>
          </div>
        )}
      </motion.div>
    </div>
  );
}
